import React from 'react';
import { useMemo } from 'react';
import { MemberShip } from '../../props';
import s from './style.module.css';
import LazyableImage from '@Components/lazyable_image/view';
import pc3LakesImage from '@Images/partner_banner/pc_3lakes.webp';
import sp3LakesImage from '@Images/partner_banner/sp_3lakes.webp';
import pcHashimotoImage from '@Images/partner_banner/pc_hashimoto.webp';
import spHashimotoImage from '@Images/partner_banner/sp_hashimoto.webp';
import pcBrickAndWoodImage from '@Images/partner_banner/pc_brickandwood.webp';
import spBrickAndWoodImage from '@Images/partner_banner/sp_brickandwood.webp';
import pcTaiheiyoImage from '@Images/partner_banner/pc_taiheiyo.webp';
import spTaiheiyoImage from '@Images/partner_banner/sp_taiheiyo.webp';
import MemberSwitch from '../member_switch/view';
import Inquiry from '@Components/inquiry/view';
import InitialPiceBox from '@Pages/Detail/components/initial_price_box/view';
import NotInitialPiceBox from '@Pages/Detail/components/not_initial_price_box/view';

type CampaignBanner = {
    pcImage: string;
    spImage: string;
    // 画像の実寸。表示幅は CSS 側で 100% にする。
    // この値は img の width/height 属性と、遅延読み込みプレースホルダの
    // aspect-ratio（--banner-ratio）の両方に使い、読み込み前後で高さを一致させる
    pcWidth: number;
    pcHeight: number;
    spWidth: number;
    spHeight: number;
    alt: string;
    url: string;
};

// 太平洋クラブグループ（/courses?series_id=10 の全23コース）＋ ロイヤルコース。
// 916 は 2026年7月に「太平洋クラブ ロイヤルコース」として共通会員制へ編入されたが、
// DB 上はまだ series_id=10 に入っておらずコース名も旧名のため、ここで明示的に含めている。
// 597=太平洋クラブ（共通）、598=太平洋アソシエイツ、599=関西エリア
const TAIHEIYO_COURSE_IDS = [
    30, 77, 78, 79, 140, 186, 187, 221, 235, 236, 265, 266, 324, 325, 334, 597, 598, 599, 600, 607, 916, 1167, 1168,
    1169,
];

const TAIHEIYO_BANNER_SIZE = { pcWidth: 2177, pcHeight: 250, spWidth: 2177, spHeight: 554 };

const CAMPAIGN_BANNERS: Record<number, CampaignBanner> = {
    469: {
        // スリーレイクスカントリークラブ
        pcImage: pc3LakesImage,
        spImage: sp3LakesImage,
        pcWidth: 2177,
        pcHeight: 250,
        spWidth: 2177,
        spHeight: 554,
        alt: 'スリーレイクスカントリークラブ 会員募集のご案内',
        url: 'https://drive.google.com/file/d/1_Z5LipSP2P2BsghXkcippGY-D36Bd59h/view?usp=sharing',
    },
    596: {
        // 橋本カントリークラブ
        pcImage: pcHashimotoImage,
        spImage: spHashimotoImage,
        pcWidth: 2175,
        pcHeight: 248,
        spWidth: 2177,
        spHeight: 553,
        alt: '橋本カントリークラブ 新規会員募集 限定200口',
        url: 'https://hashimotocc-lp.vercel.app/',
    },
    862: {
        // ブリック＆ウッドクラブ
        pcImage: pcBrickAndWoodImage,
        spImage: spBrickAndWoodImage,
        pcWidth: 2177,
        pcHeight: 250,
        spWidth: 2177,
        spHeight: 554,
        alt: 'ブリック＆ウッドクラブ 視察プレー実施中',
        url: 'https://www.brickandwood.jp/membership/',
    },
    // 太平洋クラブ 新規会員募集（2026/7〜2027/3。募集終了時は TAIHEIYO_COURSE_IDS ごと削除する）
    // reduce の初期値を型注釈しないとアキュムレータが {} と推論され、
    // 中の各エントリが CampaignBanner として型チェックされない
    ...TAIHEIYO_COURSE_IDS.reduce<Record<number, CampaignBanner>>(
        (banners, courseId) => ({
            ...banners,
            [courseId]: {
                pcImage: pcTaiheiyoImage,
                spImage: spTaiheiyoImage,
                ...TAIHEIYO_BANNER_SIZE,
                alt: '太平洋クラブ 新規会員募集 正会員1,210万円（税込）限定500口',
                url: '/recruitment/taiheiyo-club',
            },
        }),
        {}
    ),
};

type CourseInfoProps = {
    courseId: string;
    isJpy: boolean;
    memberships: MemberShip[];
    isSp: boolean;
    course?: { playguideUrl?: string };
};

const CourseInfo = (props: CourseInfoProps) => {
    const [membership, setMemberShip] = React.useState<MemberShip | undefined>(props.memberships[0]);

    const campaignBanner = useMemo(() => {
        const courseId = Number(props.courseId);
        return CAMPAIGN_BANNERS[courseId] || null;
    }, [props.courseId]);

    const findMembership = (isInitialOffering: boolean) => {
        if (!membership) return undefined;

        return props.memberships.find(
            (v) =>
                v.membershipType === membership.membershipType &&
                v.name === membership.name &&
                v.isInitialOffering === isInitialOffering
        );
    };

    const initialOfferingMembership = useMemo(() => findMembership(true), [membership]);
    const notInitialOfferingMembership = useMemo(() => findMembership(false), [membership]);

    // 遅延読み込みのプレースホルダに高さを予約させるための比率（style.module.css で使う）。
    // SP/PC の出し分けは User-Agent 判定（ApplicationController#sp?）でビューポート幅と一致しないため、
    // メディアクエリでは分岐できない。実際に描画する画像の実寸から渡す。
    const bannerRatioStyle = useMemo(() => {
        if (!campaignBanner) return undefined;
        const { pcWidth, pcHeight, spWidth, spHeight } = campaignBanner;
        const ratio = props.isSp ? `${spWidth} / ${spHeight}` : `${pcWidth} / ${pcHeight}`;
        return { '--banner-ratio': ratio } as React.CSSProperties;
    }, [campaignBanner, props.isSp]);

    if (!membership && !campaignBanner) return null;

    return (
        <>
            {membership && (
                <div className={s.switch_content}>
                    <MemberSwitch
                        onChange={(v) => setMemberShip(v)}
                        memberships={props.memberships}
                        currentMembership={membership}
                    />
                </div>
            )}
            {initialOfferingMembership && (
                <InitialPiceBox
                    membership={initialOfferingMembership}
                    courseId={props.courseId}
                    isJpy={props.isJpy}
                    playguideUrl={props.course?.playguideUrl}
                />
            )}
            {notInitialOfferingMembership && (
                <NotInitialPiceBox
                    membership={notInitialOfferingMembership}
                    courseId={props.courseId}
                    isJpy={props.isJpy}
                    playguideUrl={props.course?.playguideUrl}
                />
            )}

            {campaignBanner && (
                <a href={campaignBanner.url}>
                    <div className={s.campaign_banner} style={bannerRatioStyle}>
                        {props.isSp ? (
                            <LazyableImage
                                src={campaignBanner.spImage}
                                width={campaignBanner.spWidth}
                                height={campaignBanner.spHeight}
                                alt={campaignBanner.alt}
                            />
                        ) : (
                            <LazyableImage
                                src={campaignBanner.pcImage}
                                width={campaignBanner.pcWidth}
                                height={campaignBanner.pcHeight}
                                alt={campaignBanner.alt}
                            />
                        )}
                    </div>
                </a>
            )}
            <Inquiry courseId={props.courseId} isSp={props.isSp} />
        </>
    );
};

export default CourseInfo;
